import { useEffect, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'

import type { OrdersSearch } from './index'
import { SalesOrderForm } from '@/components/forms/sales-order-form'
import { MobileSalesOrderForm } from '@/components/orders/mobile-sales-order-form'
import { LIST_SEARCH_KEYS, recallListSearch } from '@/lib/list-search-store'
import { m } from '@/lib/paraglide/messages'

interface NewOrderSearch {
    customer_id?: string
    customer_name?: string
    customer_phone?: string
}

export const Route = createFileRoute('/_app/_authed/_sidebar/orders/new')({
    component: NewOrderPage,
    validateSearch: (search: Record<string, unknown>): NewOrderSearch => ({
        customer_id: (search.customer_id as string) || undefined,
        customer_name: (search.customer_name as string) || undefined,
        customer_phone: (search.customer_phone as string) || undefined,
    }),
})

function NewOrderPage() {
    const navigate = useNavigate()
    const { customer_id, customer_name, customer_phone } = Route.useSearch()

    // Stable mobile detection via state — avoids React hooks ordering violation.
    // Initialised synchronously from matchMedia so there's no layout flash.
    const [isMobile, setIsMobile] = useState(
        () =>
            typeof window !== 'undefined' &&
            window.matchMedia('(max-width: 767px)').matches,
    )

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)')
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
        mq.addEventListener('change', handler)
        return () => mq.removeEventListener('change', handler)
    }, [])

    // Tạo xong thì về danh sách sạch để thấy ngay đơn vừa lên; huỷ thì trả
    // người dùng về đúng bộ lọc/trang họ đang xem trước khi bấm "Tạo đơn".
    const handleSuccess = () => void navigate({ to: '/orders', search: {} })
    const handleCancel = () =>
        void navigate({
            to: '/orders',
            search:
                recallListSearch<OrdersSearch>(LIST_SEARCH_KEYS.orders) ?? {},
        })

    if (isMobile) {
        return (
            <>
                {/* Sticky header — mobile only */}
                <div className="sticky top-0 z-10 flex items-center gap-3 border-b bg-background px-4 py-3">
                    <button
                        type="button"
                        onClick={handleCancel}
                        aria-label={m.mobile_back_button()}
                        className="min-h-10 min-w-10 flex items-center justify-center text-muted-foreground hover:text-foreground"
                    >
                        <ArrowLeft className="size-5" />
                    </button>
                    <h1 className="flex-1 text-base font-semibold">
                        {m.order_new_title()}
                    </h1>
                </div>

                <MobileSalesOrderForm
                    defaultCustomerId={customer_id}
                    onSuccess={handleSuccess}
                    onCancel={handleCancel}
                />
            </>
        )
    }

    return (
        <div className="space-y-5">
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={handleCancel}
                    aria-label={m.mobile_back_button()}
                    className="flex size-9 items-center justify-center rounded-lg border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                    <ArrowLeft className="size-4" />
                </button>
                <h1 className="text-xl font-semibold tracking-tight">
                    {m.order_new_title()}
                </h1>
            </div>
            <SalesOrderForm
                defaultCustomerId={customer_id}
                defaultCustomerName={customer_name}
                defaultCustomerPhone={customer_phone}
                onSuccess={handleSuccess}
                onCancel={handleCancel}
            />
        </div>
    )
}
